import {useEffect} from 'react';

/**
 * Форматы, которые принимает backend (`ClothingImageServiceImpl#validateFile`):
 * JPEG, PNG, WebP, GIF. Список держим синхронно с сервером — иначе пользователь
 * узнаёт об отказе только после загрузки.
 */
export const ALLOWED_IMAGE_TYPES: readonly string[] = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/** Предел размера одного файла на сервере — 10 МБ. */
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;

/** Значение для `accept` у `<input type="file">` — те же форматы, что принимает сервер. */
export const IMAGE_ACCEPT = ALLOWED_IMAGE_TYPES.join(',');

/** Extension used for a pasted image whose MIME type we don't map explicitly. */
const DEFAULT_EXTENSION = 'png';

/** MIME subtype → file extension for the formats browsers put on the clipboard. */
const EXTENSION_BY_TYPE: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/heic': 'heic',
};

function extensionFor(type: string): string {
    return EXTENSION_BY_TYPE[type.toLowerCase()] ?? DEFAULT_EXTENSION;
}

/** Поддерживается ли формат файла сервером. */
export function isAllowedImageType(type: string): boolean {
    return ALLOWED_IMAGE_TYPES.includes(type.toLowerCase());
}

/**
 * Почему файл нельзя загрузить: человекочитаемый текст либо `null`, если файл годится.
 * Одна проверка на все пути попадания фото — выбор файла, камера, буфер обмена.
 */
export function imageRejectReason(file: File): string | null {
    if (!isAllowedImageType(file.type)) {
        return 'Неподдерживаемый формат. Подойдут JPEG, PNG, WebP или GIF.';
    }
    if (file.size > MAX_IMAGE_BYTES) {
        return 'Файл больше 10 МБ.';
    }
    return null;
}

/** Делит файлы на пригодные к загрузке и отклонённые с причиной. */
export function splitAcceptedImages(files: File[]): {accepted: File[]; rejected: Array<{file: File; reason: string}>} {
    const accepted: File[] = [];
    const rejected: Array<{file: File; reason: string}> = [];
    for (const file of files) {
        const reason = imageRejectReason(file);
        if (reason) rejected.push({file, reason});
        else accepted.push(file);
    }
    return {accepted, rejected};
}

/** Уникальный суффикс имени: UUID, где он есть (в jsdom `crypto` нет), иначе время плюс случайные символы. */
export function uniqueFileId(): string {
    const uuid = globalThis.crypto?.randomUUID?.();
    return uuid ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Screenshots arrive as `image.png` (or with no usable name at all), so several pasted
 * images would share one name. Give each a unique one with an extension matching its type.
 */
function named(file: File): File {
    return new File([file], `clipboard-${uniqueFileId()}.${extensionFor(file.type)}`, {
        type: file.type,
    });
}

/** Image files carried by a paste event, in clipboard order; empty when there are none. */
export function extractImageFiles(clipboardData: DataTransfer | null): File[] {
    if (!clipboardData) return [];

    const files: File[] = [];
    // `items` is the reliable source for screenshots; `files` covers browsers that
    // expose a file pasted from the OS file manager but leave `items` empty.
    const items = clipboardData.items ? Array.from(clipboardData.items) : [];
    for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) files.push(file);
        }
    }
    if (files.length === 0 && clipboardData.files) {
        for (const file of Array.from(clipboardData.files)) {
            if (file.type.startsWith('image/')) files.push(file);
        }
    }
    return files.map(named);
}

/** Элемент, в который пользователь печатает: вставка текста туда важнее картинки. */
function isTextEditable(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return true;
    return target.isContentEditable;
}

/** Есть ли в буфере текст — тогда пользователь, скорее всего, вставляет именно его. */
function carriesText(clipboardData: DataTransfer | null): boolean {
    const types = clipboardData?.types ? Array.from(clipboardData.types) : [];
    return types.includes('text/plain');
}

/**
 * Lets the user paste images from the clipboard (Ctrl+V / ⌘+V) into a form that uploads photos.
 *
 * The listener sits on `document` so the gesture works wherever the focus is — including the
 * description/comment fields of the same form. A paste that carries no image is left alone
 * (no `preventDefault`), so pasting text keeps working exactly as before. Если фокус в поле
 * ввода и в буфере есть текст (скопированный из документа фрагмент с картинкой), вставку
 * тоже не перехватываем — иначе пользователь теряет текст, который хотел вставить.
 *
 * @param onImages called with the pasted image files, in clipboard order; should be stable
 *                 (wrap in `useCallback`) to avoid re-subscribing on every render
 * @param enabled  when false no listener is attached — pass the form's open/active state
 */
export function usePasteImages(onImages: (files: File[]) => void, enabled = true): void {
    useEffect(() => {
        if (!enabled) return;

        const handlePaste = (event: ClipboardEvent) => {
            if (isTextEditable(event.target) && carriesText(event.clipboardData)) return;
            const files = extractImageFiles(event.clipboardData);
            if (files.length === 0) return;
            event.preventDefault();
            onImages(files);
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [enabled, onImages]);
}

export default usePasteImages;
